<template>
  <v-row>
    <v-col cols="12" v-if="loaderMain">
      <v-boilerplate type="table-heading, divider, list-item-two-line" />
      <v-skeleton-loader class="mt-5" transition="fade-transition" type="table" />
    </v-col>
    <v-col cols="12" v-else>
      <v-card>
        <v-toolbar color="transparent" flat height="55">
          <v-toolbar-title class="text--secondary subtitle-1 font-weight-medium">
            <v-row align="center" justify="start" no-gutters>
              <router-link class="subtitle-1 routerLink font-weight-medium" :to="{name: 'CustomerListCreate'}">Clientes</router-link>
              <v-icon class="mx-1 pt-1" color="secondary">mdi-chevron-right</v-icon>
              <span class="subtitle-1 font-weight-medium d-inline-block text-truncate" :style="$vuetify.breakpoint.width < 1200 ? 'max-width: 300px;' : 'max-width: 500px;'">{{instance.name}}</span>
            </v-row>
          </v-toolbar-title>
          <v-spacer />
          <v-toolbar-items>
            <v-row align="center" justify="center" class="mr-1">
              <v-btn class="mr-2" v-if="instance.aggregations.balance > 0" @click="dialogPending=true" outlined retain-focus-on-click><v-icon small left>mdi-email</v-icon>Enviar deuda</v-btn>
              <v-btn class="mr-2" @click="dialogCustomer=true" outlined retain-focus-on-click>Editar</v-btn>
              <v-btn class="mr-2" @click="dialogPreferences=true" outlined retain-focus-on-click><v-icon small>mdi-cog</v-icon></v-btn>
              <v-btn v-if="['active', 'pending'].includes(instance.status)" @click="deleteCustomer()" outlined retain-focus-on-click color="red darken-2">{{willBeArchived ? 'Archivar' : 'Eliminar'}}</v-btn>
            </v-row>
          </v-toolbar-items>
        </v-toolbar>
        <v-divider />
        <v-card-text class="px-0 py-0">
          <v-row class="mx-0 background">
            <v-col cols="2" class="ml-2">
              <span class="d-block mb-1">Ventas totales</span>
              <span class="body-1 font-weight-medium mr-1">$ {{instance.aggregations.sales | number}}</span>
            </v-col>
            <v-divider class="my-2" vertical />
            <v-col cols="2" class="ml-2">
              <span class="d-block mb-1">Ventas en {{currentMonth}}</span>
              <div class="body-1 font-weight-medium mt-1">$ {{instance.aggregations.sales_current_period | number}}</div>
            </v-col>
            <v-divider class="my-2" vertical />
            <v-col cols="2" class="ml-2">
              <span class="d-block mb-1">
                Balance
                <v-tooltip right max-width="200px" transition="scale-transition">
                  <template v-slot:activator="{on}">
                    <v-icon v-on="on" small color="secondary">mdi-information</v-icon>
                  </template>
                  <span>Representa el total adeudado por el cliente.</span>
                </v-tooltip>
              </span>
              <div class="body-1 font-weight-medium mt-1">$ {{instance.aggregations.balance | number}}</div>
            </v-col>
          </v-row>
          <v-divider />
          <v-row>
            <v-col cols="6">
              <v-list class="transparent">
                <v-list-item>
                  <v-list-item-title class="text--secondary subtitle-1 font-weight-medium">Información general</v-list-item-title>
                </v-list-item>
                <v-list-item>
                  <v-list-item-title>Nombre</v-list-item-title>
                  <v-list-item-subtitle>{{instance.name}}</v-list-item-subtitle>
                </v-list-item>
                <v-list-item>
                  <v-list-item-title>Dirección</v-list-item-title>
                  <v-list-item-subtitle>{{instance.address}}</v-list-item-subtitle>
                </v-list-item>
                <v-list-item>
                  <v-list-item-title>Estado</v-list-item-title>
                  <v-list-item-subtitle>{{instance.state.name}}, {{instance.country.name}}</v-list-item-subtitle>
                </v-list-item>
                <v-list-item>
                  <v-list-item-title>Teléfono</v-list-item-title>
                  <v-list-item-subtitle v-if="instance.phone">{{instance.phone}}</v-list-item-subtitle>
                  <v-list-item-subtitle class="font-italic text--disabled" v-else>Sin número de teléfono</v-list-item-subtitle>
                </v-list-item>
                <v-list-item>
                  <v-list-item-title>Código postal</v-list-item-title>
                  <v-list-item-subtitle v-if="instance.zip_code">{{instance.zip_code}}</v-list-item-subtitle>
                  <v-list-item-subtitle class="font-italic text--disabled" v-else>Sin código postal</v-list-item-subtitle>
                </v-list-item>
                <v-list-item>
                  <v-list-item-title>ID</v-list-item-title>
                  <v-list-item-subtitle>
                    <v-tooltip right transition="scale-transition">
                      <template v-slot:activator="{on}">
                        <v-btn class="font-weight-regular" @click="toClipboard(instance.id)" text color="secondary" v-on="on" :ripple="false">{{instance.id}}</v-btn>
                      </template>
                      <span>Copiar</span>
                    </v-tooltip>
                  </v-list-item-subtitle>
                </v-list-item>
                <v-list-item>
                  <v-list-item-title>Creado el</v-list-item-title>
                  <v-list-item-subtitle>{{instance.created | dateTime}}</v-list-item-subtitle>
                </v-list-item>
                <v-list-item>
                  <v-list-item-title>Actualizado el</v-list-item-title>
                  <v-list-item-subtitle>{{instance.updated | dateTime}}</v-list-item-subtitle>
                </v-list-item>
              </v-list>
            </v-col>
            <v-col cols="6">
              <v-list class="transparent">
                <v-list-item>
                  <v-list-item-title class="text--secondary subtitle-1 font-weight-medium">Información de facturación</v-list-item-title>
                </v-list-item>
                <v-list-item>
                  <v-list-item-title>Email de facturación</v-list-item-title>
                  <v-list-item-subtitle>{{instance.email}}</v-list-item-subtitle>
                </v-list-item>
                <v-list-item>
                  <v-list-item-title>Moneda de facturación y cobro</v-list-item-title>
                  <v-list-item-subtitle>{{activeCurrency.id}} - {{activeCurrency.name}}</v-list-item-subtitle>
                </v-list-item>
                <v-list-item>
                  <v-list-item-title>ID Fiscal</v-list-item-title>
                  <v-list-item-subtitle v-if="instance.tax_id">{{instance.tax_id}}</v-list-item-subtitle>
                  <v-list-item-subtitle class="font-italic text--disabled" v-else>No especificado</v-list-item-subtitle>
                </v-list-item>

                <!-- BR tax settings fields -->
                <template v-if="account.country.id === 'BR' && instance.country.id === 'BR'">
                  <v-list-item>
                    <v-list-item-title>Inscripción municipal</v-list-item-title>
                    <v-list-item-subtitle v-if="instance.tax_settings.br_municipal_tax_number">{{instance.tax_settings.br_municipal_tax_number}}</v-list-item-subtitle>
                    <v-list-item-subtitle class="font-italic text--disabled" v-else>No especificado</v-list-item-subtitle>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-title>Regimen tributario</v-list-item-title>
                    <v-list-item-subtitle v-if="instance.tax_settings.br_tax_regime">{{instance.tax_settings.br_tax_regime}}</v-list-item-subtitle>
                    <v-list-item-subtitle class="font-italic text--disabled" v-else>No especificado</v-list-item-subtitle>
                  </v-list-item>
                </template>
                <!-- end BR tax settings fields -->

                <!-- CL tax settings fields -->
                <template v-if="account.country.id === 'CL' && instance.country.id === 'CL'">
                  <v-list-item>
                    <v-list-item-title>Actividad comercial</v-list-item-title>
                    <v-list-item-subtitle v-if="instance.tax_settings.cl_activity_description">{{instance.tax_settings.cl_activity_description}}</v-list-item-subtitle>
                    <v-list-item-subtitle class="font-italic text--disabled" v-else>No especificado</v-list-item-subtitle>
                  </v-list-item>
                </template>
                <!-- end CL tax settings fields -->

                <!-- CO tax settings fields -->
                <template v-if="account.country.id === 'CO' && instance.country.id === 'CO'">
                  <v-list-item>
                    <v-list-item-title>Responsabilidad tributaria</v-list-item-title>
                    <v-list-item-subtitle v-if="instance.tax_settings.co_liability">{{instance.tax_settings.co_liability}}</v-list-item-subtitle>
                    <v-list-item-subtitle class="font-italic text--disabled" v-else>No especificado</v-list-item-subtitle>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-title>Régimen tributario</v-list-item-title>
                    <v-list-item-subtitle v-if="instance.tax_settings.co_tax_regime">{{instance.tax_settings.co_tax_regime}}</v-list-item-subtitle>
                    <v-list-item-subtitle class="font-italic text--disabled" v-else>No especificado</v-list-item-subtitle>
                  </v-list-item>
                </template>
                <!-- end CO tax settings fields -->

                <!-- MX tax settings fields -->
                <template v-if="account.country.id === 'MX' && instance.country.id === 'MX'">
                  <v-list-item>
                    <v-list-item-title>Régimen tributario</v-list-item-title>
                    <v-list-item-subtitle v-if="instance.tax_settings.mx_tax_scheme">{{instance.tax_settings.mx_tax_scheme}}</v-list-item-subtitle>
                    <v-list-item-subtitle class="font-italic text--disabled" v-else>No especificado</v-list-item-subtitle>
                  </v-list-item>
                </template>
                <!-- end MX tax settings fields -->
              </v-list>

              <v-list class="mt-8 transparent">
                <v-list-item>
                  <v-list-item-title class="text--secondary subtitle-1 font-weight-medium">Información adicional</v-list-item-title>
                </v-list-item>
                <v-list-item>
                  <v-list-item-title>Propietario del cliente</v-list-item-title>
                  <v-list-item-subtitle v-if="instance.manager">{{instance.manager}}</v-list-item-subtitle>
                  <v-list-item-subtitle class="font-italic text--disabled" v-else>No especificado</v-list-item-subtitle>
                </v-list-item>
              </v-list>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- sources list -->
      <v-card class="px-0 my-5">
        <v-toolbar color="transparent" flat height="55">
          <v-toolbar-title class="text--secondary subtitle-1 font-weight-medium">Tarjetas</v-toolbar-title>
          <v-spacer />
          <v-toolbar-items>
            <v-row align="center" justify="center" class="mr-1">
              <v-btn @click="dialogSource=true" outlined>Solicitar nueva tarjeta</v-btn>
            </v-row>
          </v-toolbar-items>
        </v-toolbar>
        <v-divider />
        <v-card-text class="pa-0">
          <source-list :show-pagination="false" :show-sort="false" />
        </v-card-text>
      </v-card>
      <!-- end sources list -->

      <!-- payments list -->
      <v-card class="pa-0 my-5">
        <v-toolbar color="transparent" flat height="55">
          <v-toolbar-title class="text--secondary subtitle-1 font-weight-medium">Pagos</v-toolbar-title>
        </v-toolbar>
        <v-divider />
        <v-card-text class="pa-0">
          <payment-list :show-pagination="false" :show-sort="false" :show-criteria="{customer: instance.id}" />
        </v-card-text>
      </v-card>
      <!-- end payments list -->

      <!-- invoices list -->
      <v-card class="px-0">
        <v-toolbar color="transparent" flat height="55">
          <v-toolbar-title class="text--secondary subtitle-1 font-weight-medium">Facturas</v-toolbar-title>
        </v-toolbar>
        <v-divider />
        <v-card-text class="pa-0">
          <invoice-list :show-pagination="false" :show-sort="false" :show-criteria="{customer: instance.id}" />
        </v-card-text>
      </v-card>
      <!-- end invoices list -->

      <!-- subscriptions list -->
      <v-card class="px-0 my-5">
        <v-toolbar color="transparent" flat height="55">
          <v-toolbar-title class="text--secondary subtitle-1 font-weight-medium">Suscripciones</v-toolbar-title>
        </v-toolbar>
        <v-divider />
        <v-card-text class="pa-0">
          <subscription-list :show-pagination="false" :show-sort="false" :show-criteria="{customer: instance.id}" />
        </v-card-text>
      </v-card>
      <!-- end subscriptions list -->

      <!-- orgunits list -->
      <v-card class="px-0 my-5 overflow-hidden">
        <v-toolbar color="transparent" flat height="55">
          <v-toolbar-title class="text--secondary subtitle-1 font-weight-medium">Unidades organizativas</v-toolbar-title>
          <v-spacer />
          <v-toolbar-items>
            <v-row align="center" justify="end" class="mr-1">
              <v-btn @click="dialogOrgunitCreate=true" outlined><v-icon left small>mdi-plus</v-icon>Nuevo</v-btn>
            </v-row>
          </v-toolbar-items>
        </v-toolbar>
        <v-divider />
        <v-card-text class="pa-0">
          <orgunit-list @dialog-create="updateDialogOrgunitCreate" :show-pagination="false" :show-sort="false" :show-criteria="{customer: instance.id}" :display-create="dialogOrgunitCreate" :customer="instance" />
        </v-card-text>
      </v-card>
      <!-- end orgunits list -->

      <!-- metadata list -->
      <v-card class="px-0 my-5">
        <v-toolbar color="transparent" flat height="55">
          <v-toolbar-title class="text--secondary subtitle-1 font-weight-medium">Metadatos</v-toolbar-title>
          <v-spacer />
          <v-toolbar-items>
            <v-row align="center" justify="center" class="mr-1">
              <v-btn @click="dialogMetadata=true" outlined>Editar</v-btn>
            </v-row>
          </v-toolbar-items>
        </v-toolbar>
        <v-divider />
        <v-card-text class="pa-0">
          <v-row>
            <v-col cols="6" v-if="Object.keys(instance.metadata).length">
              <v-list class="transparent">
                <v-list-item v-for="(v, k) in instance.metadata" :key="k">
                  <v-list-item-title>{{k}}</v-list-item-title>
                  <v-list-item-subtitle>{{v}}</v-list-item-subtitle>
                </v-list-item>
              </v-list>
            </v-col>
            <v-col cols="12" v-else class="text-center py-5">
              <v-icon class="d-block" color="secondary" size="70">mdi-code-tags</v-icon>
              <span class="d-block subtitle-1">No hay metadatos</span>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
      <!-- end metadata list -->
    </v-col>

    <!--modal update customer-->
    <v-dialog v-if="editable.id" v-model="dialogCustomer" width="700" persistent no-click-animation scrollable>
      <v-card>
        <v-card-title class="pa-0">
          <v-toolbar flat height="55">
            <v-toolbar-title class="text--secondary subtitle-1 font-weight-medium">Actualizar cliente</v-toolbar-title>
            <v-spacer />
            <v-toolbar-items>
              <v-row align="center" justify="center" class="mr-1">
                <v-btn class="ml-3" @click="dialogCustomer=false" icon small><v-icon size="24">mdi-close</v-icon></v-btn>
              </v-row>
            </v-toolbar-items>
          </v-toolbar>
        </v-card-title>
        <v-divider />
        <v-card-text class="pa-0 background">
          <v-list class="v-list-form py-6 transparent">
            <v-list-item class="py-1">
              <v-list-item-title>Nombre</v-list-item-title>
              <v-list-item-subtitle>
                <v-col cols="12">
                  <v-text-field v-model.trim="$v.editable.name.$model" :error="$v.editable.name.$error" background-color="white" outlined required single-line dense placeholder="Acme LLC" maxlength="100" hide-details />
                </v-col>
              </v-list-item-subtitle>
            </v-list-item>
            <v-list-item class="py-1">
              <v-list-item-title>País</v-list-item-title>
              <v-list-item-subtitle>
                <v-col cols="8">
                  <v-select v-model="editable.country.name" disabled single-line :items="[editable.country.name]" item-text="name" item-value="id" hide-details dense height="30" outlined background-color="white" />
                </v-col>
              </v-list-item-subtitle>
            </v-list-item>
            <v-list-item class="py-1">
              <v-list-item-title>Estado</v-list-item-title>
              <v-list-item-subtitle>
                <v-col cols="8">
                  <v-select v-model="$v.editable.state.$model" :error="$v.editable.state.$error" :items="statesList" item-text="name" item-value="id" single-line hide-details dense height="30" outlined background-color="white" no-data-text="No existen estados para el país." />
                </v-col>
              </v-list-item-subtitle>
            </v-list-item>

            <!-- BR fields -->
            <template v-if="account.country.id === 'BR'">
              <v-list-item>
                <v-list-item-title>Municipio</v-list-item-title>
                <v-list-item-subtitle class="py-1">
                  <v-col cols="8">
                    <v-autocomplete v-model="$v.editable.tax_settings.br_city_code.$model" :error="$v.editable.tax_settings.br_city_code.$error" :items="bigChoices.brMunicipality.items" :search-input.sync="bigChoices.brMunicipality.search" @keyup="filterBigChoices('brMunicipality', 'localization.brMunicipalities')" item-text="name" item-value="id" outlined background-color="white" no-data-text="No existen coincidencias para la busqueda" placeholder="Escribe para buscar..." dense hide-details />
                  </v-col>
                </v-list-item-subtitle>
              </v-list-item>
            </template>
            <!-- end BR field -->

            <v-list-item class="py-1">
              <v-list-item-title>Dirección</v-list-item-title>
              <v-list-item-subtitle>
                <v-text-field v-model.trim="$v.editable.address.$model" :error="$v.editable.address.$error" background-color="white" outlined required single-line dense maxlength="64" hide-details />
              </v-list-item-subtitle>
            </v-list-item>
            <v-list-item class="py-1">
              <v-list-item-title>Código postal</v-list-item-title>
              <v-list-item-subtitle>
                <v-col cols="4">
                  <v-text-field v-model.trim="$v.editable.zip_code.$model" :error="$v.editable.zip_code.$error" background-color="white" outlined required single-line dense maxlength="64" hide-details />
                </v-col>
              </v-list-item-subtitle>
            </v-list-item>
            <v-list-item class="mt-4">
              <v-list-item-title>
                <span class="text--secondary font-weight-medium">Información de facturación</span>
              </v-list-item-title>
            </v-list-item>
            <v-list-item class="mt-2">
              <v-list-item-title>
                Moneda de facturación y cobro
                <v-tooltip right max-width="280px" transition="scale-transition">
                  <template v-slot:activator="{on}">
                    <v-icon v-on="on" small color="secondary">mdi-information</v-icon>
                  </template>
                  <span>No podrás cambiar la moneda una vez que se guarde la información del cliente o se cree una factura del cliente.</span>
                </v-tooltip>
              </v-list-item-title>
              <v-list-item-subtitle class="py-1">
                <v-col cols="8">
                  <v-text-field :value="activeCurrency.name" disabled background-color="white" outlined single-line dense hide-details />
                </v-col>
              </v-list-item-subtitle>
            </v-list-item>
            <v-list-item>
              <v-list-item-title>
                ID Fiscal
                <v-tooltip right max-width="280px" transition="scale-transition">
                  <template v-slot:activator="{on}">
                    <v-icon v-on="on" small color="secondary">mdi-information</v-icon>
                  </template>
                  <span>RUT, VAT, NIF o TAX ID</span>
                </v-tooltip>
              </v-list-item-title>
              <v-list-item-subtitle class="py-1">
                <v-col cols="8">
                  <v-text-field v-model.trim="$v.editable.tax_id.$model" :error="$v.editable.tax_id.$error" :disabled="instance.country.has_regulation" v-dni="instance.country.id" background-color="white" outlined single-line dense maxlength="32" hide-details />
                </v-col>
              </v-list-item-subtitle>
            </v-list-item>

            <!-- BR tax settings fields -->
            <template v-if="account.country.id === 'BR' && instance.country.id === 'BR'">
              <v-list-item>
                <v-list-item-title>Inscripción municipal</v-list-item-title>
                <v-list-item-subtitle class="py-1">
                  <v-text-field v-model.trim="$v.editable.tax_settings.br_municipal_tax_number.$model" :error="$v.editable.tax_settings.br_municipal_tax_number.$error" background-color="white" outlined required single-line dense maxlength="32" hide-details />
                </v-list-item-subtitle>
              </v-list-item>
              <v-list-item>
                <v-list-item-title>Régimen tributario</v-list-item-title>
                <v-list-item-subtitle class="py-1">
                  <v-col cols="9">
                    <v-select v-model="$v.editable.tax_settings.br_tax_regime.$model" :error="$v.editable.tax_settings.br_tax_regime.$error" :items="localization.brTaxRegimes" item-text="name" item-value="id" outlined background-color="white" placeholder="Selecciona una opción" dense hide-details />
                  </v-col>
                </v-list-item-subtitle>
              </v-list-item>
            </template>
            <!-- end BR tax settings fields -->

            <v-list-item v-if="account.country.id === 'CL' && instance.country.id === 'CL'">
              <v-list-item-title>Actividad comercial</v-list-item-title>
              <v-list-item-subtitle class="py-1">
                <v-text-field v-model.trim="$v.editable.tax_settings.cl_activity_description.$model" :error="$v.editable.tax_settings.cl_activity_description.$error" background-color="white" outlined required single-line dense maxlength="64" hide-details />
              </v-list-item-subtitle>
            </v-list-item>
            <template v-if="account.country.id === 'CO' && instance.country.id === 'CO'">
              <v-list-item>
                <v-list-item-title>Responsabilidad tributaria</v-list-item-title>
                <v-list-item-subtitle class="py-1">
                  <v-col cols="9">
                    <v-select v-model="$v.editable.tax_settings.co_liability.$model" :error="$v.editable.tax_settings.co_liability.$error" :items="localization.coLiabilities" item-text="name" item-value="id" outlined background-color="white" placeholder="Selecciona una opción" dense hide-details />
                  </v-col>
                </v-list-item-subtitle>
              </v-list-item>
              <v-list-item>
                <v-list-item-title>Régimen tributario</v-list-item-title>
                <v-list-item-subtitle class="py-1">
                  <v-col cols="9">
                    <v-select v-model="$v.editable.tax_settings.co_tax_regime.$model" :error="$v.editable.tax_settings.co_tax_regime.$error" :items="localization.coTaxRegimes" item-text="name" item-value="id" outlined background-color="white" placeholder="Selecciona una opción" dense hide-details />
                  </v-col>
                </v-list-item-subtitle>
              </v-list-item>
            </template>
            <v-list-item v-if="account.country.id === 'MX' && instance.country.id === 'MX'">
              <v-list-item-title>Régimen fiscal</v-list-item-title>
              <v-list-item-subtitle class="py-1">
                <v-text-field v-model.trim="$v.editable.tax_settings.mx_tax_scheme.$model" :error="$v.editable.tax_settings.mx_tax_scheme.$error" background-color="white" outlined required single-line dense maxlength="64" hide-details />
              </v-list-item-subtitle>
            </v-list-item>
            <v-list-item class="mt-4">
              <v-list-item-title>
                <span class="text--secondary font-weight-medium">Información adicional</span>
              </v-list-item-title>
            </v-list-item>
            <v-list-item class="mt-2">
              <v-list-item-title>Propietario del cliente</v-list-item-title>
              <v-list-item-subtitle class="py-1">
                <v-col cols="8">
                  <v-text-field v-model.trim="$v.editable.manager.$model" :error="$v.editable.manager.$error" outlined required single-line background-color="white" dense maxlength="64" hide-details />
                </v-col>
              </v-list-item-subtitle>
            </v-list-item>
            <v-list-item class="mt-2">
              <v-list-item-title>Email de facturación</v-list-item-title>
              <v-list-item-subtitle class="py-1">
                <v-col cols="8">
                  <v-text-field v-model.trim="$v.editable.email.$model" :error="$v.editable.email.$error" outlined required single-line background-color="white" dense maxlength="64" hide-details />
                </v-col>
              </v-list-item-subtitle>
            </v-list-item>
            <v-list-item>
              <v-list-item-title>Teléfono</v-list-item-title>
              <v-list-item-subtitle class="py-1">
                <v-col cols="8">
                  <v-text-field v-model.trim="$v.editable.phone.$model" :error="$v.editable.phone.$error" background-color="white" outlined required single-line dense v-if="editable.country" v-mask="`${editable.country.phone_code} # #### ####`" maxlength="16" hide-details />
                </v-col>
              </v-list-item-subtitle>
            </v-list-item>
            <v-list-item>
              <v-list-item-title>Sitio web</v-list-item-title>
              <v-list-item-subtitle class="py-1">
                <v-col cols="8">
                  <v-text-field v-model.trim="$v.editable.website.$model" :error="$v.editable.website.$error" background-color="white" outlined required single-line class="pa-0" dense  maxlength="64" :hide-details="true" />
                </v-col>
              </v-list-item-subtitle>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-divider />
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn outlined @click="dialogCustomer=false">Cancelar</v-btn>
          <v-btn @click="patchCustomer" :loading="$store.state.customers.createLoader" color="primary">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- modal update customer -->

    <!-- dialog preferences -->
    <v-dialog v-if="editable.id" v-model="dialogPreferences" width="520" persistent no-click-animation scrollable>
      <v-card>
        <v-card-title class="pa-0">
          <v-toolbar flat height="55">
            <v-toolbar-title class="text--secondary subtitle-1 font-weight-medium">Preferencias del cliente</v-toolbar-title>
            <v-spacer />
            <v-toolbar-items>
              <v-row align="center" justify="center" class="mr-1">
                <v-btn class="ml-3" :disabled="savingPreferences" @click="dialogPreferences=false" icon small><v-icon size="24">mdi-close</v-icon></v-btn>
              </v-row>
            </v-toolbar-items>
          </v-toolbar>
        </v-card-title>
        <v-divider />
        <v-card-text class="pa-0 background">
          <v-list class="pb-2 transparent">
            <v-list-item class="py-1">
              <v-list-item-content>
                <span class="subtitle-2 text--secondary font-weight-medium">Mensajería</span>
                <span class="caption text--secondary mt-1">Selecciona qué tipos de correos puede recibir este cliente. Si deshabilitas una opción, dejará de recibir estos emails automáticamente.</span>
              </v-list-item-content>
            </v-list-item>
            <v-list-item class="py-1">
              <v-list-item-title>Facturas</v-list-item-title>
              <v-list-item-action>
                <v-switch v-model="sending.send_invoices" class="ml-n4 mt-n4" style="position:absolute" />
              </v-list-item-action>
            </v-list-item>
            <v-list-item class="py-1">
              <v-list-item-title>Cobranzas</v-list-item-title>
              <v-list-item-action>
                <v-switch v-model="sending.send_collections" class="ml-n4 mt-n4" style="position:absolute" />
              </v-list-item-action>
            </v-list-item>
            <v-list-item class="py-1">
              <v-list-item-title>Comprobantes de pago</v-list-item-title>
              <v-list-item-action>
                <v-switch v-model="sending.send_vouchers" class="ml-n4 mt-n4" style="position:absolute" />
              </v-list-item-action>
            </v-list-item>
          </v-list>
          <v-divider />
          <v-list class="pb-4 transparent">
            <v-list-item class="py-1">
              <v-list-item-content>
                <v-row align="center" no-gutters>
                  <span class="subtitle-2 text--secondary font-weight-medium">Documentos de soporte</span>
                  <piriod-beta-label class="ml-2" element="Chip" feature="SupportingDocuments" />
                </v-row>
                <span class="caption text--secondary mt-1">Define cómo debemos solicitar la OC o HES que este cliente debe emitir antes del vencimiento.</span>
              </v-list-item-content>
            </v-list-item>
            <v-list-item class="py-1">
              <v-list-item-title>Solicitar OC/HES automáticamente</v-list-item-title>
              <v-list-item-action>
                <v-switch v-model="supportingDocumentsRule.auto_request" class="ml-n4 mt-n4" style="position:absolute" />
              </v-list-item-action>
            </v-list-item>
            <v-list-item class="py-1">
              <v-list-item-title>Días de anticipación</v-list-item-title>
              <v-list-item-subtitle class="py-1">
                <v-col cols="5" class="px-0 ml-auto">
                  <v-text-field v-model.number="$v.supportingDocumentsRule.days_before.$model" :error="$v.supportingDocumentsRule.days_before.$error" :disabled="!supportingDocumentsRule.auto_request" suffix="días" background-color="white" outlined required single-line dense hide-details />
                </v-col>
              </v-list-item-subtitle>
            </v-list-item>
            <v-list-item class="py-1">
              <v-list-item-title>Aceptar OC</v-list-item-title>
              <v-list-item-action>
                <v-switch v-model="supportingDocumentsRule.use_document" class="ml-n4 mt-n4" style="position:absolute" />
              </v-list-item-action>
            </v-list-item>
            <v-list-item class="py-1">
              <v-list-item-title>Aceptar HES</v-list-item-title>
              <v-list-item-action>
                <v-switch v-model="supportingDocumentsRule.use_hes" class="ml-n4 mt-n4" style="position:absolute" />
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-divider />
        <v-card-actions class="pa-4">
          <v-btn :disabled="savingPreferences" outlined @click="dialogPreferences=false">Cancelar</v-btn>
          <v-spacer />
          <v-btn :loading="savingPreferences" color="primary" @click="savePreferences">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- dialog preferences -->

    <!-- dialog source -->
    <v-dialog v-model="dialogSource" width="500" persistent no-click-animation scrollable>
      <v-card>
        <v-card-title class="pa-0">
          <v-toolbar flat height="55">
            <v-toolbar-title class="subtitle-1 text--secondary font-weight-medium">Solicitar nueva tarjeta</v-toolbar-title>
          </v-toolbar>
        </v-card-title>
        <v-divider />
        <v-card-text class="px-0 py-3 background">
          <v-list class="transparent" v-if="!$store.state.customers.retrieveLoader">
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title class="ml-1 text--secondary subtitle-2 font-weight-medium">Para</v-list-item-title>
                <v-list-item-subtitle class="ml-1 body-2 text--primary font-weight-regular no-ellipsis">Enviaremos la solicitud de inscripción a este email</v-list-item-subtitle>
                <v-list-item-subtitle>
                  <v-select v-model="$v.source.recipient.$model" :error="$v.source.recipient.$error" :items="contactsList.concat({ name: 'Email de facturación', email: instance.email })" item-text="name" item-value="email" outlined background-color="white" dense hide-details>
                    <template v-slot:selection="{item}">
                      <v-chip class="my-1" color="secondary" small>
                        <span>{{item.name}} &lt;{{item.email}}&gt;</span>
                      </v-chip>
                    </template>
                    <template v-slot:item="{item}">
                      <span class="body-2">{{item.name}} &lt;{{item.email}}&gt;</span>
                    </template>
                  </v-select>
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title class="ml-1 text--secondary subtitle-2 font-weight-medium">Descripción</v-list-item-title>
                <v-list-item-subtitle class="ml-1 body-2 text--primary font-weight-regular no-ellipsis">Esta descripción le dará a entender a tu cliente la razón de solicitud de inscripción de su tarjeta de crédito</v-list-item-subtitle>
                <v-list-item-subtitle>
                  <v-text-field v-model="$v.source.description.$model" :error="$v.source.description.$error" placeholder="Escribe una descripción para la solicitud" maxlength="256" background-color="white" outlined required single-line dense hide-details />
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title class="ml-1 text--secondary subtitle-2 font-weight-medium">Procesador de pagos</v-list-item-title>
                <v-list-item-subtitle class="ml-1 body-2 text--primary font-weight-regular no-ellipsis">Los cargos futuros hechos a la tarjeta serán recibidos a través del procesador de pagos que selecciones</v-list-item-subtitle>
                <v-list-item-subtitle class="mx-1 font-weight-regular">
                  <v-radio-group v-model="$v.source.gateway.$model" hide-details>
                    <v-sheet class="my-1 pa-2 overflow-hidden rounded" v-for="p in providersList" :key="p.gateway" outlined>
                      <v-radio :value="p.gateway">
                        <template v-slot:label>
                          <v-row class="pl-3" align="center" justify="space-between">
                            <img :src="require(`@/assets/appAsset-brand--${p.gateway}.svg`)" :alt="gateway" height="26">
                          </v-row>
                        </template>
                      </v-radio>
                    </v-sheet>
                  </v-radio-group>
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-divider />
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn @click="dialogSource=false" outlined>Cancelar</v-btn>
          <v-btn @click="postSource" :loading="loaderSource" color="primary">Solicitar tarjeta</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- end dialog source -->

    <!-- dialog metadata -->
    <v-dialog v-if="editable.id" v-model="dialogMetadata" width="700" persistent no-click-animation scrollable>
      <v-card>
        <v-card-title class="pa-0">
          <v-toolbar flat height="55">
            <v-toolbar-title class="subtitle-1 text--secondary font-weight-medium">Editar metadatos</v-toolbar-title>
          </v-toolbar>
        </v-card-title>
        <v-divider />
        <v-card-text class="py-4 background">
          <v-row no-gutters v-if="editableMetadata.length" align="center">
            <v-col cols="4">
              <span class="body-2 text--secondary font-weight-medium ml-1">Clave</span>
            </v-col>
            <v-col cols="8">
              <span class="body-2 text--secondary font-weight-medium ml-1">Valor</span>
            </v-col>
          </v-row>
          <v-row no-gutters v-for="(d, i) in editableMetadata" :key="i" align="center">
            <v-col cols="4" class="py-1">
              <v-text-field v-model.trim="$v.editableMetadata.$each[i].key.$model" :error="$v.editableMetadata.$each[i].key.$error" background-color="white" outlined required single-line dense maxlength="32" hide-details />
            </v-col>
            <v-col cols="7" class="py-1">
              <v-text-field v-model.trim="$v.editableMetadata.$each[i].value.$model" :error="$v.editableMetadata.$each[i].value.$error" background-color="white" outlined required single-line dense maxlength="256" hide-details />
            </v-col>
            <v-col cols="1" class="py-1 text-right">
              <v-btn v-if="i >= 1 || editableMetadata.length > 1" @click="editableMetadata.splice(i, 1)" icon color="secondary"><v-icon small>mdi-delete</v-icon></v-btn>
            </v-col>
            <v-col cols="12" v-if="!$v.editableMetadata.$each[i].key.itsRepeated">
              <span class="d-block red--text caption ml-1 mt-n1 mb-1"><v-icon color="red" small left>mdi-alert-circle</v-icon>La clave debe ser única.</span>
            </v-col>
          </v-row>
          <v-row no-gutters>
            <v-col cols="3">
              <v-btn @click="editableMetadata.push({ key: '', value: '' })" :disabled="editableMetadata.length >= 10" text color="primary" :ripple="false"><v-icon left>mdi-plus</v-icon>Agregar elemento</v-btn>
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider />
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn @click="dialogMetadata=false" outlined>Cancelar</v-btn>
          <v-btn @click="updateMetadata" :loading="loaderMetadata" :disabled="!editableMetadata.length" color="primary">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- end dialog source -->

    <!-- dialog pending -->
    <v-dialog v-if="!$store.state.customers.retrieveLoader" v-model="dialogPending" width="700" persistent no-click-animation scrollable>
      <v-card>
        <v-toolbar flat height="55">
          <v-toolbar-title class="subtitle-1 text--secondary font-weight-medium">Enviar deuda<v-chip class="ml-3" color="primary" text-color="white" small>Beta</v-chip></v-toolbar-title>
          <v-spacer />
          <v-toolbar-items>
            <v-row align="center" justify="center" class="mr-1">
              <v-btn @click="dialogPending=false" icon small><v-icon size="24">mdi-close</v-icon></v-btn>
            </v-row>
          </v-toolbar-items>
        </v-toolbar>
        <v-divider />
        <v-card-subtitle class="py-2 px-3 background">
          <v-row align="start" no-gutters>
            <v-col cols="12">
              <v-autocomplete class="ma-0" v-model="$v.pending.recipients.$model" :items="contactsList.concat({ name: 'Email de facturación', email: instance.email })" :error="$v.pending.recipients.$error" prefix="Para" small-chips deletable-chips :hide-details="!$v.pending.recipients.$error" required multiple chips item-text="name" item-value="email"
                hint="Selecciona al menos un destinatario" no-data-text="No existen contactos asociados al cliente" persistent-hint autofocus dense outlined color="blue-grey lighten-2" background-color="white">
                <template v-slot:item="data">
                  <template v-if="data.item.orgunit">{{data.item.name}} de {{data.item.orgunit.name}} ({{data.item.email}})</template>
                  <template v-else>{{data.item.name}} ({{data.item.email}})</template>
                </template>
              </v-autocomplete>
            </v-col>
          </v-row>
          <v-row align="center" no-gutters>
            <v-col cols="12">
              <v-text-field class="mx-0" prefix="Asunto" v-model="$v.pending.subject.$model" :error="$v.pending.subject.$error" background-color="white" outlined required single-line dense maxlength="60" hint="Escribe un asunto para el mensaje" persistent-hint :hide-details="!$v.pending.subject.$error" />
            </v-col>
          </v-row>
        </v-card-subtitle>
        <v-divider />
        <v-card-text>
          <v-row class="mx-0 py-5" align="center" justify="center">
            <v-col cols="10">
              <reminder-pending-email :instance="instance" :template="account.email_template" :action="pending.action" :color-background="account.color_background" @update:message="pending.message=$event" :message.sync="pending.message" />
            </v-col>
          </v-row>
          <v-row class="mx-0 pb-2" justify="center">
            <v-col cols="10">
              <span class="mr-2 body-2 text--secondary">Archivos adjuntos</span>
              <v-chip class="ma-1" color="secondary" small outlined><v-icon left small>mdi-paperclip</v-icon>*.pdf</v-chip>
              <span class="ml-2 caption">(se adjuntan todas las facturas en formato PDF)</span>
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider />
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn @click="dialogPending=false" outlined>Cancelar</v-btn>
          <v-btn @click="createPending" :loading="$store.state.reminders.createLoader" color="primary">Enviar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- end dialog pending -->

  </v-row>
</template>
<script>
import { mapState } from 'vuex'
import { required, requiredIf, email, url, maxLength, minLength, integer, minValue, maxValue } from 'vuelidate/lib/validators'
import { normalizeTaxId } from '@/locale/tax-id'
import { validateZipCode } from '@/locale/zip-code'
import cloneDeep from 'lodash/cloneDeep'

import InvoiceList from '@/components/invoice/InvoiceList'
import OrgunitList from '@/components/customer/OrgunitList'
import PaymentList from '@/components/payments/PaymentList'
import PiriodBetaLabel from '@/components/ux/PiriodBetaLabel'
import ReminderPendingEmail from '@/components/reminders/pendings/Email'
import SourceList from '@/components/payments/SourceList'
import SubscriptionList from '@/components/subscription/SubscriptionList'

import GenericViewMixin from '@/mixins/GenericViewMixin'

export default {
  mixins: [
    GenericViewMixin
  ],
  inject: ['theme'],
  components: {
    InvoiceList,
    OrgunitList,
    PiriodBetaLabel,
    ReminderPendingEmail,
    PaymentList,
    SourceList,
    SubscriptionList,
    VBoilerplate: {
      functional: true,
      render (h, { data, props, children }) {
        return h('v-skeleton-loader', {
          ...data,
          props: {
            boilerplate: true,
            elevation: 2,
            ...props
          }
        }, children)
      }
    }
  },
  data: () => ({
    activeCurrency: {},
    bigChoices: {
      brMunicipality: {
        search: null,
        items: []
      }
    },
    customerStatus: null,
    dialogCustomer: false,
    dialogMetadata: false,
    dialogOrgunitCreate: false,
    dialogPending: false,
    dialogPreferences: false,
    dialogSource: false,
    editable: {},
    editableMetadata: [{ key: '', value: '' }],
    loaderMain: false,
    loaderMetadata: false,
    loaderSource: false,
    localization: {
      brMunicipalities: [],
      brTaxRegimes: [],
      coLiabilities: [],
      coTaxRegimes: []
    },
    pending: {
      action: 'pay',
      message: 'Según nuestros registros tienes las siguientes facturas pendientes de pago:',
      recipients: [],
      subject: ''
    },
    savingPreferences: false,
    sending: {
      send_invoices: false,
      send_collections: false,
      send_vouchers: false
    },
    supportingDocumentsRule: {
      id: null,
      auto_request: true,
      days_before: 30,
      use_document: true,
      use_hes: false
    },
    source: {
      description: '',
      gateway: null,
      recipient: ''
    },
    taxSettings: [
      {
        country: 'BR',
        fields: ['br_city_code']
      },
      {
        country: 'BR',
        fields: ['br_municipal_tax_number']
      },
      {
        country: 'BR',
        fields: ['br_tax_regime']
      },
      {
        country: 'CL',
        fields: ['cl_activity_description']
      },
      {
        country: 'CO',
        fields: ['co_liability', 'co_tax_regime']
      },
      {
        country: 'MX',
        fields: ['mx_tax_scheme']
      }
    ]
  }),
  computed: {
    ...mapState({
      contactsList: state => state.customers.contactsList,
      instance: state => state.customers.customersDetail,
      providersList: state => state.payments.providersList,
      statesList: state => state.generals.statesList,
      subscriptionsList: state => state.subscriptions.subscriptionsList
    }),
    willBeArchived () {
      if (!this.customerStatus) return false
      if (this.customerStatus.sales > 0) return true
      if (this.customerStatus.has_valuable_sources) return true
      if (this.customerStatus.has_archivable_abandoned_sources) return true
      return this.subscriptionsList.some(s => ['cancelled', 'finalized'].includes(s.status))
    }
  },
  created () {
    this.loaderMain = true

    this.$store.dispatch('customers/RETRIEVE', {
      resource: 'customers',
      id: this.$route.params.id
    })
    .then(() => {
      const customer = cloneDeep(this.instance)
      const taxSettings = this.taxSettings.find(field => field.country === customer.country.id)

      if (taxSettings) {
        taxSettings.fields.forEach(field => {
          if (!customer.tax_settings[field]) {
            customer.tax_settings[field] = null
          }
        })
      }

      this.editable = customer
      this.editable.tax_id = normalizeTaxId(this.editable.country.id, this.editable.tax_id)
      this.editableMetadata = Object.keys(this.editable.metadata).map((k) => {
        return { key: k, value: this.editable.metadata[k] }
      })

      // set sending values
      this.sending = {
        send_invoices: this.editable.send_invoices,
        send_collections: this.editable.send_collections,
        send_vouchers: this.editable.send_vouchers
      }

      this.loadSupportingDocumentsRule()
    })
    .then(() => {
      /**
       * Retrieves the list of states that belong to the current customer's country.
       */
      this.$store.dispatch('generals/LIST', {
        resource: 'generals/states',
        query: {
          country: this.instance.country.id,
          page_size: 1000
        }
      })
    })
    .then(() => {
      /**
       * Retrieves the list of sources that belong to the current customer.
       */
      this.$store.dispatch('payments/LIST', {
        resource: 'sources',
        query: {
          id__in: this.instance.sources.join(','),
          status: 'chargeable'
        }
      })
    })
    .then(() => {
      /**
       * Retrieves the customer status (drives the delete/archive button label).
       */
      this.$store.dispatch('customers/RETRIEVE', {
        resource: 'customers',
        id: this.$route.params.id,
        detail: 'status',
        setDetail: false,
        loader: false
      })
      .then(response => { this.customerStatus = response.data })
    })
    .then(() => {
      /**
       * Retrieves the list of contacts that belong to the current customer.
       */
      this.$store.dispatch('customers/LIST', {
        resource: 'contacts',
        query: {
          customer: this.$route.params.id
        }
      })
    })
    .then(() => {
      /**
       * Retrieves the list of payments that belong to the current customer.
       */
      this.$store.dispatch('payments/LIST', {
        resource: 'payments',
        query: {
          customer: this.$route.params.id,
          'invoice__status!': 'draft',
          page_size: 5
        }
      })
    })
    .then(() => {
      /**
       * Retrieves the list of invoices that belong to the current customer.
       */
      this.$store.dispatch('invoices/LIST', {
        resource: 'invoices',
        query: {
          customer: this.$route.params.id,
          page_size: 5
        }
      })
    })
    .then(() => {
      /**
       * Retrieves the list of subscriptions that belong to the current customer.
       */
      this.$store.dispatch('subscriptions/LIST', {
        resource: 'subscriptions',
        query: {
          customer: this.$route.params.id,
          page_size: 5
        }
      })
    })
    .then(() => {
      /**
       * Retrieves the list of orgunits that belong to the current customer.
       */
      this.$store.dispatch('customers/LIST', {
        resource: 'orgunits',
        query: {
          customer: this.$route.params.id,
          page_size: 5
        }
      })
    })
    .then(() => {
      /**
       * Retrieves the list of payment providers that allow reusable
       * use and support the currency of the current instance.
       */
      this.$store.dispatch('payments/LIST', {
        resource: 'providers',
        query: {
          gateway__allow_reusable_use: 'True',
          gateway__currencies__in: [this.instance.currency]
        }
      })
    })
    .then(() => {
      /**
       * Retrieves the list of currencies that are supported by
       * the current instance.
       */
      this.activeCurrency = this.currencies.find(c => c.id === this.instance.currency)
    })
    .then(() => {
      /**
       * Conditionally imports the 'br' locale module and assigns the 'brMunicipalities' property to the 'localization' object.
       * This is done when the country ID of the current account is 'BR' (Brazil).
       * If the import is successful, the 'brMunicipalities' property is assigned the value from the imported module.
       * If there is an error during the import, the error is logged to the console.
       */
      if (this.account.country.id === 'BR') {
        import('../../locale/br')
          .then(module => {
            this.localization.brMunicipalities = module.brMunicipalities
            this.localization.brTaxRegimes = module.brTaxRegimes

            /**
             * Sets the items of the `brMunicipality` property in `bigChoices` to the municipality
             * that matches the `br_city_code` of the current instance's tax settings.
             */
            this.bigChoices.brMunicipality.items = [this.localization.brMunicipalities.find(m => m.id === this.instance.tax_settings.br_city_code)]
          })
          .catch(error => {
            console.error('Error loading one or many BR localization objects: ', error)
          })
      }

      if (this.account.country.id === 'CO') {
        import('../../locale/co')
          .then(module => {
            this.localization.coLiabilities = module.coLiabilities
            this.localization.coTaxRegimes = module.coTaxRegimes
          })
          .catch(error => {
            console.error('Error loading one or many CO localization objects: ', error)
          })
      }
    })
    .finally(() => {
      this.loaderMain = false
    })
  },
  methods: {
    deleteCustomer () {
      const isArchive = this.willBeArchived
      const archiveText = `
        <span class="d-block my-4">El cliente será archivado y no se podrá utilizar.</span>
        <span class="d-block my-4 font-weight-medium">Esta acción no se puede deshacer.</span>
      `
      const deleteText = `
        <span class="d-block my-4">El cliente será eliminado permanentemente.</span>
        <span class="d-block my-4 font-weight-medium">Esta acción no se puede deshacer.</span>
      `

      this.$dialog.warning({
        title: isArchive ? 'Archivar cliente' : 'Eliminar cliente',
        text: isArchive ? archiveText : deleteText,
        actions: [
          {
            color: 'red',
            text: isArchive ? 'Archivar' : 'Eliminar',
            handle: () => {
              return this.$store.dispatch('customers/DESTROY', {
                resource: 'customers',
                id: this.$route.params.id
              })
              .then(() => {
                return this.$store.dispatch('customers/RETRIEVE', {
                  resource: 'customers',
                  id: this.$route.params.id,
                  loader: false
                })
                .then(() => {
                  this.$dialog.message.info('El cliente ha sido archivado')
                })
                .catch((error) => {
                  if (error.response && error.response.status === 404) {
                    this.$dialog.message.info('El cliente ha sido eliminado')
                    this.$router.push({ name: 'CustomerListCreate' })
                  }
                })
              })
              .catch((error) => {
                if (error.response && error.response.status === 400) {
                  this.$dialog.message.error(error.response.data.non_field_errors[0])
                }
              })
            }
          }
        ]
      })
    },
    createPending () {
      // validate
      this.$v.pending.$touch()
      if (this.$v.pending.$invalid) {
        return false
      }

      // set customer id to pending
      const payload = Object.assign({}, this.pending)
      payload.customer = this.instance.id

      this.$store.dispatch('reminders/CREATE', {
        resource: 'reminders/pendings',
        payload: payload
      })
      .then(() => {
        this.dialogPending = false
      })
      .catch((error) => {
        this.$dialog.message.error(this.parseErrors(error.response.data))
      })
    },
    patchCustomer () {
      this.$v.editable.$touch()

      if (this.$v.editable.$invalid) {
        return false
      }

      const payload = {
        address: this.editable.address,
        email: this.editable.email,
        manager: this.editable.manager,
        name: this.editable.name,
        phone: this.editable.phone,
        state: this.editable.state.id || this.editable.state,
        tax_settings: this.editable.tax_settings,
        website: this.editable.website,
        zip_code: this.editable.zip_code
      }

      if (!this.instance.country.has_regulation) {
        payload.tax_id = normalizeTaxId(this.instance.country.id, this.editable.tax_id)
      }

      /**
       * Updates the `br_city_name` property of the `payload.tax_settings` object
       * if the `this.account.country.id` is equal to 'BR'.
       * @param {Object} payload - The payload object containing tax settings.
       */
      if (this.account.country.id === 'BR') {
        const brCityCode = this.localization.brMunicipalities.find(m => m.id === payload.tax_settings.br_city_code)
        payload.tax_settings.br_city_name = brCityCode.name
      }

      this.$store.dispatch('customers/UPDATE', {
        resource: 'customers',
        id: this.$route.params.id,
        payload: payload
      })
      .then(() => {
        this.dialogCustomer = false
        this.$dialog.message.info('El cliente ha sido actualizado')
      })
      .catch(() => {
        this.$dialog.message.error('Error al actualizar el cliente.')
      })
    },
    loadSupportingDocumentsRule () {
      this.$store.dispatch('supportingDocuments/LIST', {
        resource: 'supporting-documents/rules',
        query: { customer: this.instance.id, page_size: 1 },
        setState: false,
        loader: false
      })
      .then((response) => {
        const existing = (response.data.results || [])[0]
        if (!existing) return
        this.supportingDocumentsRule = {
          id: existing.id,
          auto_request: !!existing.auto_request,
          days_before: existing.days_before || 30,
          use_document: !!existing.use_document,
          use_hes: !!existing.use_hes
        }
      })
      .catch(() => {})
    },
    savePreferences () {
      this.$v.supportingDocumentsRule.$touch()
      if (this.$v.supportingDocumentsRule.$invalid) return
      this.savingPreferences = true

      const messagingPromise = this.$store.dispatch('customers/UPDATE', {
        resource: 'customers',
        id: this.$route.params.id,
        payload: this.sending
      })

      const rulePayload = {
        auto_request: this.supportingDocumentsRule.auto_request,
        days_before: this.supportingDocumentsRule.auto_request
          ? this.supportingDocumentsRule.days_before
          : 0,
        use_document: this.supportingDocumentsRule.use_document,
        use_hes: this.supportingDocumentsRule.use_hes
      }
      const rulePromise = this.supportingDocumentsRule.id
        ? this.$store.dispatch('supportingDocuments/UPDATE', {
            resource: 'supporting-documents/rules',
            id: this.supportingDocumentsRule.id,
            payload: rulePayload
          })
        : this.$store.dispatch('supportingDocuments/CREATE', {
            resource: 'supporting-documents/rules',
            payload: Object.assign({}, rulePayload, { customer: this.instance.id }),
            commitInstance: false
          })

      Promise.allSettled([messagingPromise, rulePromise])
        .then(([messagingResult, ruleResult]) => {
          if (ruleResult.status === 'fulfilled' && !this.supportingDocumentsRule.id && ruleResult.value && ruleResult.value.data) {
            this.supportingDocumentsRule.id = ruleResult.value.data.id
          }

          const errors = []
          if (messagingResult.status === 'rejected') {
            const data = messagingResult.reason && messagingResult.reason.response && messagingResult.reason.response.data
            errors.push(`Mensajería: ${data ? this.parseErrors(data) : 'error desconocido'}`)
          }
          if (ruleResult.status === 'rejected') {
            const data = ruleResult.reason && ruleResult.reason.response && ruleResult.reason.response.data
            errors.push(`Documentos de soporte: ${data ? this.parseErrors(data) : 'error desconocido'}`)
          }

          if (errors.length) {
            this.$dialog.message.error(errors.join(' · '))
            return
          }
          this.dialogPreferences = false
          this.$dialog.message.info('Preferencias actualizadas.')
        })
        .finally(() => {
          this.savingPreferences = false
        })
    },
    postSource () {
      this.$v.source.$touch()
      if (this.$v.source.$invalid) {
        return false
      }
      const payload = {
        customer: this.instance.id,
        description: this.source.description,
        gateway: this.source.gateway,
        usage: 'reusable'
      }
      this.$dialog.warning({
        title: 'Confirmar solicitar tarjeta',
        text: `
          <span class="d-block mb-4">Al confirmar se enviará un email solicitando a
          <b>${this.instance.name}</b>
          la inscripción de su tarjeta de crédito por
          <b>${this.source.description}</b></span>
          <span class="d-block my-4">¿Estás seguro de continuar?</span>
          <span class="d-block font-weight-medium">Esta acción no se puede deshacer</span>
        `,
        actions: [
          {
            color: 'primary',
            text: 'Continuar',
            handle: () => {
              this.loaderSource = true
              this.$store.dispatch('payments/CREATE', {
                resource: 'sources',
                payload: payload
              })
              .then((response) => {
                this.$store.dispatch('payments/CREATE', {
                  resource: 'sources',
                  id: response.data.id,
                  detail: 'send_inscription_request',
                  payload: {
                    recipient: this.source.recipient
                  }
                })
                .then(() => {
                  this.$dialog.message.info('Solicitud de tarjeta enviada')
                })
                .finally(() => {
                  this.dialogSource = false
                  this.loaderSource = false

                  // reset validation
                  this.$v.source.$reset()

                  // reset source
                  this.source = { description: '', gateway: null, recipient: '' }
                })
              })
              .catch((error) => {
                this.$dialog.message.error(this.parseErrors(error.response.data))
              })
            }
          }
        ]
      })
    },
    updateDialogOrgunitCreate (value) {
      this.dialogOrgunitCreate = value
    },
    updateMetadata () {
      this.$v.editableMetadata.$touch()
      if (this.$v.editableMetadata.$invalid) {
        return false
      }
      this.loaderMetadata = true
      const payload = Object.fromEntries(this.editableMetadata.map(({ key, value }) => [key, value]))
      this.$store.dispatch('customers/UPDATE', {
        resource: 'customers',
        id: this.instance.id,
        payload: {
          metadata: payload
        }
      })
      .then(() => {
        this.dialogMetadata = false
        this.$dialog.message.info('El cliente ha sido actualizado')
      })
      .catch((error) => {
        this.$dialog.message.error(this.parseErrors(error.response.data))
      })
      .finally(() => {
        this.loaderMetadata = false
      })
    }
  },
  validations: {
    editable: {
      name: {
        required,
        maxLength: maxLength(100)
      },
      state: {
        required
      },
      address: {
        required,
        maxLength: maxLength(64)
      },
      tax_settings: {
        br_city_code: {
          required: requiredIf(function () {
            return this.account.country.id === 'BR' && this.instance.country.id === 'BR'
          }),
          maxLength: maxLength(12)
        },
        br_municipal_tax_number: {
          maxLength: maxLength(32)
        },
        br_tax_regime: {
          required: requiredIf(function () {
            return this.account.country.id === 'BR' && this.instance.country.id === 'BR'
          }),
          maxLength: maxLength(32)
        },
        cl_activity_description: {
          required: requiredIf(function () {
            return this.account.country.id === 'CL' && this.instance.country.id === 'CL'
          }),
          maxLength: maxLength(64)
        },
        co_liability: {
          required: requiredIf(function () {
            return this.account.country.id === 'CO' && this.instance.country.id === 'CO'
          }),
          maxLength: maxLength(32)
        },
        co_tax_regime: {
          required: requiredIf(function () {
            return this.account.country.id === 'CO' && this.instance.country.id === 'CO'
          }),
          maxLength: maxLength(32)
        },
        mx_tax_scheme: {
          required: requiredIf(function () {
            return this.account.country.id === 'MX' && this.instance.country.id === 'MX'
          }),
          maxLength: maxLength(3)
        }
      },
      tax_id: {
        maxLength: maxLength(32)
      },
      manager: {
        maxLength: maxLength(64)
      },
      email: {
        required,
        email,
        maxLength: maxLength(254)
      },
      phone: {
        maxLength: maxLength(16)
      },
      website: {
        url
      },
      zip_code: {
        zip: function (val) {
          if (this.instance.country.id) {
            return validateZipCode(this.instance.country.id, val)
          }

          return true
        }
      }
    },
    editableMetadata: {
      $each: {
        key: {
          required,
          maxLength: maxLength(32),
          itsRepeated: function (value) {
            if (value) {
              return (this.editableMetadata.filter(d => d.key === value).length === 1)
            }
            return true
          }
        },
        value: {
          required,
          maxLength: maxLength(256)
        }
      },
      minLength: minLength(1),
      maxLength: maxLength(10)
    },
    source: {
      recipient: {
        required,
        email
      },
      description: {
        required,
        maxLength: maxLength(256)
      },
      gateway: {
        required
      }
    },
    pending: {
      recipients: {
        required,
        maxLength: maxLength(8)
      },
      subject: {
        required,
        minLength: minLength(6),
        maxLength: maxLength(60)
      },
      message: {
        required
      }
    },
    supportingDocumentsRule: {
      days_before: {
        required: requiredIf(function () {
          return this.supportingDocumentsRule.auto_request
        }),
        integer,
        minValue: minValue(1),
        maxValue: maxValue(365)
      }
    }
  }
}
</script>
